/* iter-118 — Universal file preview thumbnail (+ iter-123 hover popover).
 *
 * Renders a small/large image preview for uploaded files (Pictures, PDFs,
 * Word, Excel, PowerPoint) by hitting GET /api/uploads/{filename}/preview.
 * Gracefully falls back to a generic lucide icon if the file type cannot be
 * previewed or the preview endpoint errors.
 *
 * iter-123 — hovering the tile for >220ms opens a 480×360 Quick Look
 * popover (anchored to the cursor and clamped to the viewport) so users
 * can see what's inside without clicking through. Disabled automatically
 * for non-previewable file types and on touch devices.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FileText, FileImage, Film, FileArchive, FileSpreadsheet, FileType2, File as FileIcon,
} from 'lucide-react';

const PREVIEWABLE_EXTS = new Set([
  'jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'tiff',
  'pdf',
  'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp', 'rtf',
]);

function basename(url) {
  if (!url) return '';
  const clean = String(url).split('?')[0].split('#')[0];
  const parts = clean.split('/');
  return parts[parts.length - 1] || '';
}

function extOf(name) {
  if (!name) return '';
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot + 1).toLowerCase();
}

function iconFor(ext) {
  if (['png', 'jpg', 'jpeg', 'webp', 'gif', 'svg', 'bmp', 'tiff'].includes(ext)) return FileImage;
  if (['mp4', 'webm', 'mov', 'mkv', 'avi'].includes(ext)) return Film;
  if (['zip', 'tar', 'gz', '7z', 'rar'].includes(ext)) return FileArchive;
  if (['xls', 'xlsx', 'csv', 'ods'].includes(ext)) return FileSpreadsheet;
  if (['doc', 'docx', 'odt', 'rtf', 'txt', 'md'].includes(ext)) return FileType2;
  if (['pdf'].includes(ext)) return FileText;
  return FileIcon;
}

const SIZE_MAP = {
  sm: { box: 'w-8 h-8', icon: 'w-4 h-4' },
  md: { box: 'w-14 h-14', icon: 'w-6 h-6' },
  lg: { box: 'w-24 h-24', icon: 'w-8 h-8' },
  xl: { box: 'w-40 h-32', icon: 'w-10 h-10' },
};

const POPOVER_W = 480;
const POPOVER_H = 360;
const HOVER_DELAY_MS = 220;

function renderIcon(ext, iconClass, color) {
  const Cmp = iconFor(ext);
  return <Cmp className={iconClass} style={{ color }} />;
}

/**
 * Props:
 *  - url:           Full or relative URL to the uploaded file.
 *  - filename:      Original filename. Optional.
 *  - size:          'sm' (32px) | 'md' (56px) | 'lg' (96px) | 'xl' (160px).
 *  - color:         Accent color for the fallback icon.
 *  - rounded:       Tailwind rounding class (defaults to 'rounded-md').
 *  - className:     Extra classes for the outer wrapper.
 *  - hoverPreview:  Show a 480×360 Quick Look popover on hover (default true).
 */
export default function FilePreviewThumb({
  url,
  filename = '',
  size = 'sm',
  color = '#4A5D5D',
  rounded = 'rounded-md',
  className = '',
  hoverPreview = true,
}) {
  const [errored, setErrored] = useState(false);
  const [hoverPos, setHoverPos] = useState(null);
  const hoverTimerRef = useRef(null);
  const wrapperRef = useRef(null);

  const stored = basename(url);
  const ext = extOf(stored || filename);

  const { box, icon } = SIZE_MAP[size] || SIZE_MAP.sm;

  const previewable = PREVIEWABLE_EXTS.has(ext) && !!stored;
  const previewUrl = previewable
    ? `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${encodeURIComponent(stored)}/preview`
    : null;

  // iter-123 — only enable hover preview on devices with a real pointer
  // (skip touch + smaller-than-popover viewports). Computed lazily so
  // SSR doesn't blow up if it ever runs.
  const hasFinePointer =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const hoverEnabled = hoverPreview && previewable && !errored && hasFinePointer;

  // Cleanup pending timer on unmount or when hover-preview turns off.
  useEffect(() => () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
  }, []);

  const computePopoverPos = (e) => {
    const margin = 12;
    let x = (e?.clientX ?? 0) + 18;
    let y = (e?.clientY ?? 0) + 18;
    const maxX = window.innerWidth - POPOVER_W - margin;
    const maxY = window.innerHeight - POPOVER_H - margin;
    if (x > maxX) x = Math.max(margin, (e?.clientX ?? 0) - POPOVER_W - 18);
    if (y > maxY) y = Math.max(margin, (e?.clientY ?? 0) - POPOVER_H - 18);
    return { x: Math.max(margin, x), y: Math.max(margin, y) };
  };

  const handleMouseEnter = (e) => {
    if (!hoverEnabled) return;
    const pos = computePopoverPos(e);
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setHoverPos(pos), HOVER_DELAY_MS);
  };
  const handleMouseMove = (e) => {
    if (!hoverEnabled || !hoverPos) return;
    setHoverPos(computePopoverPos(e));
  };
  const handleMouseLeave = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setHoverPos(null);
  };

  const wrapperCls = `relative flex items-center justify-center overflow-hidden flex-shrink-0 ${box} ${rounded} ${className}`;
  const commonProps = {
    ref: wrapperRef,
    className: wrapperCls,
    style: { background: '#F4F1EB', border: '1px solid #EAE5D9' },
    onMouseEnter: handleMouseEnter,
    onMouseMove: handleMouseMove,
    onMouseLeave: handleMouseLeave,
  };

  const popover = hoverPos && previewUrl && typeof document !== 'undefined'
    ? createPortal(
        <div
          data-testid={`file-preview-hover-${stored}`}
          className="fixed pointer-events-none z-[9999] rounded-xl shadow-2xl overflow-hidden"
          style={{
            left: hoverPos.x,
            top: hoverPos.y,
            width: POPOVER_W,
            background: 'white',
            border: '1px solid rgba(0,0,0,0.08)',
            boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
          }}
        >
          <img
            src={previewUrl}
            alt={filename || stored}
            style={{ width: '100%', height: POPOVER_H, objectFit: 'contain', background: '#F4F1EB' }}
          />
          <div className="px-3 py-2 text-[12px] font-bold truncate" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>
            {filename || stored}
          </div>
        </div>,
        document.body,
      )
    : null;

  if (previewUrl && !errored) {
    return (
      <>
        <div
          {...commonProps}
          data-testid={`file-preview-thumb-${stored}`}
        >
          <img
            src={previewUrl}
            alt={filename || stored}
            loading="lazy"
            className="w-full h-full object-cover"
            onError={() => setErrored(true)}
          />
        </div>
        {popover}
      </>
    );
  }

  return (
    <div
      {...commonProps}
      data-testid={`file-preview-fallback-${stored || filename}`}
    >
      {renderIcon(ext, icon, color)}
    </div>
  );
}
